/**
 * Settings → Client portal → Rental agreement (P7 + #216). Per Hollywood Trucks company:
 *   - whether the company uses the client portal at all (Eric 2026-10-30: "CA and IL only" at launch);
 *   - the library document clients e-sign (unset = they sign on paper);
 *   - who countersigns, saved only with "I confirm this person is authorized";
 *   - optionally, the signatory's prebuilt signature image, stamped on the signature certificate.
 *     Without one the signatory's name is typed in a signature font (Eric, 2026-10-30). One upload
 *     can cover all three companies.
 * Countersigning is automatic once a company has a signatory; the card says how it signs. Every change is audited on the server; without the superadmin membership the
 * section reads only. L2: each card ends with who changed it last (the page's shared history,
 * reloaded through onChanged after each saved change).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  fetchSignatureImage, getAgreementSettings, removeSignatureImage, sameSignatory, saveAgreementSettings, savePortalEnabled, uploadSignatureImage,
  type AgreementSettings, type AgreementSettingsResponse, type EntityAgreementSettings,
} from '@/lib/portalAgreementSettingsApi';
import type { EntityKey } from '@/lib/portalCoiSettingsApi';
import { historyKey } from '@/lib/portalSettingsHistoryApi';
import { CardHistory } from '@/components/settings/portal/SettingsHistory';
import { entriesFor, type SettingsHistoryState } from '@/hooks/useSettingsHistory';

const NOT_SET = '__none__';
const day = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Whether a company countersigns on its own (the server's countersignReady: a signatory is enough),
 * and how its signature is drawn: the saved image, or the signatory's name typed in a signature font.
 */
function readiness(s: EntityAgreementSettings): string {
  if (!s.signatory) return 'Not ready: needs a signatory';
  if (s.signature && sameSignatory(s.signature.signatory_name, s.signatory.name)) return 'Ready: countersigns automatically with the signature image';
  if (s.signature) {
    return `Ready: countersigns automatically with ${s.signatory.name}’s name typed (the image on file is ${s.signature.signatory_name}’s, so it isn’t used)`;
  }
  return `Ready: countersigns automatically with ${s.signatory.name}’s name typed in a signature font`;
}

function SignaturePreview({ entity, sha256 }: { entity: EntityKey; sha256: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    let url: string | null = null;
    fetchSignatureImage(entity)
      .then((blob) => { if (!live) return; url = URL.createObjectURL(blob); setSrc(url); setFailed(false); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [entity, sha256]);
  if (failed) return <p className="text-xs text-muted-foreground">The saved image couldn’t be shown. It is still on file.</p>;
  if (!src) return <div className="h-14 w-44 animate-pulse rounded border border-border bg-muted/40" aria-hidden />;
  // A light ground, so a dark-ink transparent PNG reads the same as on the PDF.
  return <img src={src} alt="Signature image on file" className="h-14 max-w-[220px] rounded border border-border bg-white object-contain p-1" />;
}

interface CardProps {
  entity: EntityKey;
  label: string;
  legalName: string | null;
  setting: EntityAgreementSettings;
  documents: AgreementSettingsResponse['documents'];
  documentsUnavailable: boolean;
  canEdit: boolean;
  /** The other companies with the same saved signatory: one upload can cover them too. */
  alsoFor: EntityKey[];
  onSettings: (next: AgreementSettings) => void;
  /** Whether this company uses the client portal (undefined: not reported, shown as nothing). */
  portalEnabled?: boolean;
  onPortal: (next: Record<EntityKey, boolean>) => void;
  history?: SettingsHistoryState;
}

/**
 * Eric 2026-10-30: "CA and IL only" at launch. While a company is off, its clients don't see their
 * bookings in the portal and staff can't share them; turning it on later is this one switch.
 */
function PortalSwitch({ entity, enabled, canEdit, onPortal }: { entity: EntityKey; enabled: boolean; canEdit: boolean; onPortal: CardProps['onPortal'] }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      onPortal((await savePortalEnabled(entity, !enabled)).portal);
      toast.success(enabled ? 'Client portal turned off.' : 'Client portal turned on.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'That couldn’t be saved. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium text-foreground">Client portal</span>
        <p className="text-xs text-muted-foreground">
          {enabled
            ? 'On: clients see their bookings for this company in the portal.'
            : 'Off: this company’s clients don’t see their bookings in the portal, and staff can’t share them.'}
        </p>
      </div>
      {canEdit && (
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => void toggle()}>
          {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
          {enabled ? 'Turn the client portal off' : 'Turn the client portal on'}
        </Button>
      )}
    </div>
  );
}

function EntityAgreementCard({ entity, label, legalName, setting, documents, documentsUnavailable, canEdit, alsoFor, onSettings, portalEnabled, onPortal, history }: CardProps) {
  const [name, setName] = useState(setting.signatory?.name ?? '');
  const [title, setTitle] = useState(setting.signatory?.title ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [forAll, setForAll] = useState(true);
  const [busy, setBusy] = useState<null | 'agreement' | 'signatory' | 'image'>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const id = `agreement-${entity}`;

  const changedSignatory = name.trim() !== (setting.signatory?.name ?? '') || title.trim() !== (setting.signatory?.title ?? '');
  const canSaveSignatory = Boolean(name.trim() && title.trim() && confirmed && changedSignatory) && busy === null;

  const run = useCallback(async (kind: 'agreement' | 'signatory' | 'image', work: () => Promise<AgreementSettings>, done: string) => {
    setBusy(kind);
    try {
      onSettings(await work());
      toast.success(done);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'That couldn’t be saved. Try again.';
      if (kind === 'image') setImageError(message); else toast.error(message);
      return false;
    } finally {
      setBusy(null);
    }
  }, [onSettings]);

  const chooseAgreement = (value: string) => run('agreement', async () => (await saveAgreementSettings({ entity_key: entity, agreement_document_id: value === NOT_SET ? null : value })).settings, 'Agreement saved.');
  const saveSignatory = async () => {
    const ok = await run('signatory', async () => (await saveAgreementSettings({ entity_key: entity, signatory: { name: name.trim(), title: title.trim() }, confirm_authority: true })).settings, 'Signatory saved.');
    if (ok) setConfirmed(false);
  };
  const upload = (file: File | undefined) => {
    if (!file) return;
    setImageError(null);
    const keys: EntityKey[] = forAll ? [entity, ...alsoFor] : [entity];
    void run('image', async () => (await uploadSignatureImage(file, keys)).settings, 'Signature image saved.').finally(() => {
      if (fileRef.current) fileRef.current.value = '';
    });
  };
  const remove = () => { setImageError(null); void run('image', async () => (await removeSignatureImage(entity)).settings, 'Signature image removed.'); };

  return (
    <section aria-label={label} className="flex flex-col gap-4 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h4 className="text-sm font-semibold">{label}</h4>
          {legalName && <p className="text-xs text-muted-foreground">Countersigns as {legalName}</p>}
        </div>
        <p className="text-xs text-muted-foreground" role="status">{readiness(setting)}</p>
      </div>

      {portalEnabled !== undefined && <PortalSwitch entity={entity} enabled={portalEnabled} canEdit={canEdit} onPortal={onPortal} />}

      <div className="flex max-w-md flex-col gap-1.5">
        <span className="text-xs font-medium text-foreground">Agreement clients e-sign</span>
        {canEdit ? (
          <Select value={setting.agreement?.document_id ?? NOT_SET} onValueChange={chooseAgreement} disabled={busy !== null || documentsUnavailable}>
            <SelectTrigger aria-label="Agreement clients sign"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NOT_SET}>Not set: clients sign on paper</SelectItem>
              {documents.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-sm">{documents.find((d) => d.id === setting.agreement?.document_id)?.title ?? (setting.agreement ? 'A library document' : 'Not set: clients sign on paper')}</p>
        )}
        {documentsUnavailable && <p className="text-xs text-muted-foreground">The Sales library can’t be reached right now, so the agreement can’t be changed.</p>}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground">Countersigned by</span>
        {setting.signatory && (
          <p className="text-sm">{setting.signatory.name}, {setting.signatory.title} <span className="text-xs text-muted-foreground">· confirmed {day(setting.signatory.confirmed_at)}</span></p>
        )}
        {canEdit && (
          <div className="flex flex-col gap-2">
            <div className="grid gap-2 sm:grid-cols-2">
              <Input id={`${id}-name`} aria-label="Signatory’s full name" placeholder="Full name" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
              <Input id={`${id}-title`} aria-label="Title" placeholder="Title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <input type="checkbox" className="mt-0.5 accent-[#8FB560]" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              I confirm this person is authorized to sign agreements for this company.
            </label>
            <div>
              <Button type="button" variant="outline" size="sm" disabled={!canSaveSignatory} onClick={saveSignatory}>
                {busy === 'signatory' && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
                Save signatory
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground">Signature image</span>
        {/* The signature itself is shown only to those who may change it (the server enforces it too). */}
        {setting.signature && canEdit && <SignaturePreview entity={entity} sha256={setting.signature.sha256} />}
        {setting.signature && !canEdit && <p className="text-xs text-muted-foreground">A signature image is on file.</p>}
        {!setting.signature && <p className="text-xs text-muted-foreground">None. Optional: the signatory’s name is typed in a signature font instead.</p>}
        {setting.signature && <p className="text-xs text-muted-foreground">Saved as {setting.signature.signatory_name}’s signature</p>}
        {canEdit && (
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/png"
              className="sr-only"
              id={`${id}-file`}
              disabled={!setting.signatory}
              aria-label={setting.signature ? 'Replace the signature image (transparent PNG)' : 'Upload a signature image (transparent PNG)'}
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <Button type="button" variant="outline" size="sm" disabled={busy !== null || !setting.signatory} onClick={() => fileRef.current?.click()}>
              {busy === 'image' && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
              {setting.signature ? 'Replace image' : 'Upload image'}
            </Button>
            {setting.signatory && alsoFor.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" className="accent-[#8FB560]" checked={forAll} onChange={(e) => setForAll(e.target.checked)} />
                {alsoFor.length === 1
                  ? `Also use it for the other company ${setting.signatory.name} signs for`
                  : `Also use it for the ${alsoFor.length} other companies ${setting.signatory.name} signs for`}
              </label>
            )}
            {setting.signature && (
              <Button type="button" variant="ghost" size="sm" disabled={busy !== null} onClick={remove}>Remove signature image</Button>
            )}
          </div>
        )}
        {canEdit && (
          <p className="text-xs text-muted-foreground">
            {setting.signatory ? 'Optional: a transparent PNG of the signature, up to 1 MB.' : 'Save the signatory first. The image is saved as their signature.'}
          </p>
        )}
        {imageError && <p role="alert" className="text-xs text-[#E0556C]">{imageError}</p>}
      </div>
      {history && <CardHistory label={label} entries={entriesFor(history, historyKey.agreement(entity))} unavailable={history.unavailable} />}
    </section>
  );
}

interface Props {
  /** The page's change history (L2); without it the cards show none. */
  history?: SettingsHistoryState;
  /** Called after each saved change, so the page reloads the history. */
  onChanged?: () => void;
}

export function RentalAgreementSettings({ history, onChanged }: Props = {}) {
  const [data, setData] = useState<AgreementSettingsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // State changes only after the read settles (never synchronously inside the effect).
  const fetchSettings = useCallback(() => getAgreementSettings().then(
    (res) => { setData(res); setLoadError(null); },
    (err) => setLoadError(err instanceof Error ? err.message : 'The agreement settings could not be loaded.'),
  ), []);
  useEffect(() => { void fetchSettings(); }, [fetchSettings]);
  const load = () => { setLoadError(null); void fetchSettings(); };

  const onSettings = useCallback((settings: AgreementSettings) => {
    setData((d) => (d ? { ...d, settings } : d));
    onChanged?.();
  }, [onChanged]);
  const onPortal = useCallback((portal: Record<EntityKey, boolean>) => {
    setData((d) => (d ? { ...d, portal } : d));
    onChanged?.();
  }, [onChanged]);

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4 md:p-6">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-foreground">Rental agreement</h3>
          <p className="text-xs text-muted-foreground">Clients e-sign the agreement chosen here. Hollywood Trucks countersigns automatically as the signatory below, with their signature image if one is saved, or their name typed in a signature font.</p>
        </div>
        {loadError && (
          <div className="flex flex-col items-start gap-2 text-sm text-muted-foreground">
            <p role="alert">The agreement settings couldn’t be loaded. {loadError}</p>
            <Button type="button" variant="outline" size="sm" onClick={load}>Try again</Button>
          </div>
        )}
        {!data && !loadError && <p className="text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />Loading…</p>}
        {data && !data.can_edit && <p className="text-xs text-muted-foreground">Only a superadmin can change the agreement or who signs it.</p>}
        {data?.entities.map((e) => (
          <EntityAgreementCard
            key={e.key}
            entity={e.key}
            label={e.label}
            legalName={e.legal_name}
            setting={data.settings[e.key]}
            documents={data.documents}
            documentsUnavailable={Boolean(data.documents_unavailable)}
            canEdit={data.can_edit}
            alsoFor={data.entities
              .map((o) => o.key)
              .filter((k) => k !== e.key && sameSignatory(data.settings[k]?.signatory?.name, data.settings[e.key]?.signatory?.name))}
            onSettings={onSettings}
            portalEnabled={data.portal?.[e.key]}
            onPortal={onPortal}
            history={history}
          />
        ))}
      </CardContent>
    </Card>
  );
}
